// pages/PlanosPagamento/ListaPlanosPagamento.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  ButtonGroup,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { usePlanosPagamento } from '../../contexts/PlanosPagamentoContext';
import { formatarMoeda } from '../../utils/formatters';

const ListaPlanosPagamento = () => {
  const navigate = useNavigate();
  const { planosPagamento, loading, carregarPlanosPagamento, excluirPlanoPagamento } = usePlanosPagamento();
  
  // Estados
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedPlano, setSelectedPlano] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);
  const [tipoPlano, setTipoPlano] = useState('BASE');

  // Carregar planos com base no tipo selecionado
  const loadPlanos = useCallback(async () => {
    try {
      setError(null);
      await carregarPlanosPagamento({ tipo: tipoPlano });
    } catch (err) {
      console.error('Erro ao carregar planos:', err);
      setError('Não foi possível carregar os planos de pagamento.');
    }
  }, [carregarPlanosPagamento, tipoPlano]);

  useEffect(() => {
    let mounted = true;

    const initializeData = async () => {
      if (mounted) {
        await loadPlanos();
      }
    };

    initializeData();

    return () => {
      mounted = false;
    };
  }, [loadPlanos]);

  // Handlers do menu
  const handleOpenMenu = useCallback((event, plano) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedPlano(plano);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchorEl(null);
    setSelectedPlano(null);
  }, []);

  // Ações do menu
  const handleView = useCallback(() => {
    if (selectedPlano) {
      handleCloseMenu();
      navigate(`/planos-pagamento/${selectedPlano.id}`);
    }
  }, [navigate, selectedPlano, handleCloseMenu]);

  const handleEdit = useCallback(() => {
    if (selectedPlano) {
      handleCloseMenu();
      navigate(`/planos-pagamento/editar/${selectedPlano.id}`);
    }
  }, [navigate, selectedPlano, handleCloseMenu]);

  const handleDelete = useCallback(() => {
    handleCloseMenu();
    setConfirmDelete(true);
  }, [handleCloseMenu]);

  const handleVincularEstudante = useCallback(() => {
    if (selectedPlano) {
      handleCloseMenu();
      navigate(`/planos-pagamento/${selectedPlano.id}/vincular-estudante`);
    }
  }, [navigate, selectedPlano, handleCloseMenu]);

  // Confirmação de exclusão
  const confirmDeletePlano = useCallback(async () => {
    try {
      if (selectedPlano) {
        await excluirPlanoPagamento(selectedPlano.id);
        setConfirmDelete(false);
        await loadPlanos();
      }
    } catch (err) {
      console.error('Erro ao excluir plano:', err);
      setError('Erro ao excluir plano de pagamento.');
    }
  }, [selectedPlano, excluirPlanoPagamento, loadPlanos]);

  // Renderizar botão de novo plano
  const renderNovoPlanoButton = () => {
    if (tipoPlano === 'BASE') {
      return (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/planos-pagamento/criar')}
        >
          Novo Plano Base
        </Button>
      );
    } else {
      return (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/planos-pagamento/selecionar-base')}
        >
          Novo Plano Individual
        </Button>
      );
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho com filtros */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Planos de Pagamento
          </Typography>
          <ButtonGroup variant="outlined" size="small">
            <Button
              variant={tipoPlano === 'BASE' ? 'contained' : 'outlined'}
              onClick={() => setTipoPlano('BASE')}
            >
              Planos Base
            </Button>
            <Button
              variant={tipoPlano === 'INDIVIDUAL' ? 'contained' : 'outlined'}
              onClick={() => setTipoPlano('INDIVIDUAL')}
            >
              Planos Individuais
            </Button>
          </ButtonGroup>
        </Box>
        {renderNovoPlanoButton()}
      </Box>

      {/* Mensagem de erro */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Lista de planos */}
      <Grid container spacing={3}>
        {planosPagamento
          .filter(plano => plano.tipo === tipoPlano)
          .map((plano) => (
            <Grid item xs={12} md={6} lg={4} key={plano.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="h6">{plano.anoLetivo}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {plano.tipo === 'BASE' ? 'Plano Base' : 'Plano Individual'}
                      </Typography>
                    </Box>
                    <IconButton 
                      onClick={(e) => handleOpenMenu(e, plano)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {plano.descricao}
                  </Typography>
                  
                  <Typography variant="h5" color="primary">
                    {formatarMoeda(plano.valorTotal)}
                  </Typography>
                  
                  {plano.tipo === 'INDIVIDUAL' && plano.estudante && (
                    <Chip 
                      label={`Estudante: ${plano.estudante.nome}`}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Prestações: {plano.prestacoes?.length || 0}
                    </Typography>
                    <Typography variant="body2">
                      Início: {new Date(plano.dataInicio).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2">
                      Fim: {new Date(plano.dataFim).toLocaleDateString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}

        {(!planosPagamento || planosPagamento.length === 0) && (
          <Grid item xs={12}>
            <Alert severity="info">
              {tipoPlano === 'BASE' 
                ? 'Nenhum plano base cadastrado.'
                : 'Nenhum plano individual encontrado.'
              }
            </Alert>
          </Grid>
        )}
      </Grid>

      {/* Menu de opções */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handleView}>
          <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
          Visualizar
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Editar
        </MenuItem>
        {selectedPlano?.tipo === 'BASE' && (
          <MenuItem onClick={handleVincularEstudante}>
            <PersonAddIcon sx={{ mr: 1 }} fontSize="small" />
            Vincular Estudante
          </MenuItem>
        )}
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Excluir
        </MenuItem>
      </Menu>

      {/* Diálogo de confirmação de exclusão */}
      <Dialog 
        open={confirmDelete} 
        onClose={() => setConfirmDelete(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir este plano de pagamento?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={confirmDeletePlano} 
            color="error" 
            variant="contained"
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListaPlanosPagamento;






